import { useEffect, useRef, useState } from 'react';

type Direction = 'left' | 'right';

interface UseHorizontalScrollOptions {
    scrollAmount?: number;
    selectedIndex?: number;
}

export function useHorizontalScroll<T extends HTMLElement = HTMLDivElement>(
    deps: any[] = [],
    options: UseHorizontalScrollOptions = {}
) {
    const { scrollAmount, selectedIndex } = options;

    const containerRef = useRef<T>(null);
    const [hasOverflowLeft, setHasOverflowLeft] = useState(false);
    const [hasOverflowRight, setHasOverflowRight] = useState(false);

    const checkOverflow = () => {
        const container = containerRef.current;
        if (container) {
            setHasOverflowLeft(container.scrollLeft > 0);
            setHasOverflowRight(
                container.scrollWidth > container.offsetWidth &&
                container.scrollLeft < container.scrollWidth - container.offsetWidth
            );
        }
    };

    const scroll = (direction: Direction) => {
        const container = containerRef.current;
        if (container) {
            const amount = scrollAmount ?? container.offsetWidth;
            container.scrollBy({
                left: direction === 'left' ? -amount : amount,
                behavior: 'smooth',
            });
        }
    };

    const scrollToElement = (element: HTMLElement) => {
        const container = containerRef.current;
        if (!container || !element) return;

        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        // Verificar si está visible
        if (elementRect.left >= containerRect.left && elementRect.right <= containerRect.right) {
            return;
        }

        // Scroll calculado
        const offset = elementRect.left - containerRect.left + container.scrollLeft;
        container.scrollTo({
            left: offset - container.offsetWidth / 2 + element.offsetWidth / 2,
            behavior: 'smooth',
        });
    };

    useEffect(() => {
        if (selectedIndex === undefined || selectedIndex === null) return;
        const container = containerRef.current;
        if (!container) return;

        const items = Array.from(container.children) as HTMLElement[];
        const target = items[selectedIndex];
        if (target) {
            scrollToElement(target);
        }
    }, [selectedIndex]);

    useEffect(() => {
        checkOverflow();
        const handleResize = () => checkOverflow();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, deps);

    useEffect(() => {
        checkOverflow();
    }, deps);

    return {
        containerRef,
        hasOverflowLeft,
        hasOverflowRight,
        scroll,
        scrollToElement,
        checkOverflow
    };
}